import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Clock, CheckCircle, AlertTriangle, Calendar,
  User, Search, Filter, ChevronDown, X, MessageSquare, Bell,
  Send, Loader2, Trash2, Edit, Eye, RefreshCw, Mail, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TaskHub = () => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [loading, setLoading] = useState(false);
  const [taskStats, setTaskStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [reminders, setReminders] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Forms
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [showNewReminder, setShowNewReminder] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(null);
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to_id: '',
    priority: 'normal',
    due_date: '',
    category: 'other',
    related_to_type: '',
    related_to_id: '',
    reminder_date: ''
  });
  
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'general',
    placeholders: []
  });
  
  const [newReminder, setNewReminder] = useState({
    title: '',
    description: '',
    reminder_date: '',
    reminder_time: ''
  });

  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchTaskStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/dashboard`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTaskStats(data);
      }
    } catch (error) {
      console.error('Error fetching task stats:', error);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employees`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/email-templates`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEmailTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/reminders`, { headers });
      if (res.ok) {
        const data = await res.json();
        setReminders(data);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    fetchTaskStats();
    fetchTasks();
    fetchEmployees();
    fetchEmailTemplates();
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTask = async () => {
    if (!newTask.title || !newTask.assigned_to_id) {
      toast.error('Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/enhanced`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify(newTask)
      });
      
      if (res.ok) {
        toast.success('Task created');
        setShowNewTask(false);
        setNewTask({
          title: '', description: '', assigned_to_id: '', priority: 'normal',
          due_date: '', category: 'other', related_to_type: '', related_to_id: '', reminder_date: ''
        });
        fetchTasks();
        fetchTaskStats();
      } else {
        toast.error('Failed to create task');
      }
    } catch (error) {
      toast.error('Error creating task');
    }
    setLoading(false);
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}/status?status=${status}`, {
        method: 'PUT',
        headers,
        
      });
      if (res.ok) {
        toast.success(`Task marked as ${status}`);
        fetchTasks();
        fetchTaskStats();
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name || !newTemplate.subject || !newTemplate.body) {
      toast.error('Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/email-templates`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify(newTemplate)
      });
      
      if (res.ok) {
        toast.success('Template created');
        setShowNewTemplate(false);
        setNewTemplate({ name: '', subject: '', body: '', category: 'general', placeholders: [] });
        fetchEmailTemplates();
      }
    } catch (error) {
      toast.error('Error creating template');
    }
    setLoading(false);
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/email-templates/${templateId}`, {
        method: 'DELETE',
        headers,
        
      });
      if (res.ok) {
        toast.success('Template deleted');
        fetchEmailTemplates();
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const createReminder = async () => {
    if (!newReminder.title || !newReminder.reminder_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/reminders`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify(newReminder)
      });
      
      if (res.ok) {
        toast.success('Reminder created');
        setShowNewReminder(false);
        setNewReminder({ title: '', description: '', reminder_date: '', reminder_time: '' });
        fetchReminders();
      }
    } catch (error) {
      toast.error('Error creating reminder');
    }
    setLoading(false);
  };

  const dismissReminder = async (reminderId) => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/reminders/${reminderId}/dismiss`, {
        method: 'PUT',
        headers,
        
      });
      if (res.ok) {
        toast.success('Reminder dismissed');
        fetchReminders();
      }
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (statusFilter !== 'all' && task.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;
    return true;
  });

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      normal: 'bg-blue-100 text-blue-700 border-blue-300',
      low: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[priority] || colors.normal;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || colors.pending;
  };

  const tabs = [
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'templates', label: 'Email Templates', icon: Mail },
    { id: 'reminders', label: 'My Reminders', icon: Bell },
  ];

  return (
    <div className="space-y-6" data-testid="task-hub">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-teal-100 text-teal-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`task-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          {taskStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white p-4 rounded-xl border shadow-sm">
                <p className="text-sm text-gray-500">Total Tasks</p>
                <p className="text-2xl font-bold text-gray-800">{taskStats.total}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                <p className="text-sm text-yellow-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-700">{taskStats.pending}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                <p className="text-sm text-blue-600">In Progress</p>
                <p className="text-2xl font-bold text-blue-700">{taskStats.in_progress}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <p className="text-sm text-green-600">Completed</p>
                <p className="text-2xl font-bold text-green-700">{taskStats.completed}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                <p className="text-sm text-red-600">Overdue</p>
                <p className="text-2xl font-bold text-red-700">{taskStats.overdue}</p>
              </div>
            </div>
          )}

          {/* Filters & Actions */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={() => setShowNewTask(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="new-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
          </div>

          {/* Tasks List */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : filteredTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-12">No tasks found</p>
            ) : (
              <div className="divide-y">
                {filteredTasks.map(task => (
                  <div key={task.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(task.priority)}`}>
                            {task.priority.toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </span>
                          {task.category && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              {task.category}
                            </span>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-800">{task.title}</h4>
                        {task.description && (
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {task.assigned_to_name}
                          </span>
                          {task.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {task.due_date}
                            </span>
                          )}
                          {task.related_to_name && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {task.related_to_type}: {task.related_to_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {task.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => updateTaskStatus(task.id, 'in_progress')}>
                            Start
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateTaskStatus(task.id, 'completed')}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Email Templates</h3>
            <Button onClick={() => setShowNewTemplate(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="new-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {emailTemplates.map(template => (
              <div key={template.id} className="bg-white p-4 rounded-xl border shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-800">{template.name}</h4>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                      {template.category}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowSendEmail(template)} className="text-teal-600 hover:text-teal-700">
                      <Send className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteTemplate(template.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 font-medium">Subject: {template.subject}</p>
                <div className="mt-2 text-xs text-gray-500">
                  Placeholders: {template.placeholders?.join(', ') || 'None'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reminders Tab */}
      {activeTab === 'reminders' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">My Reminders</h3>
            <Button onClick={() => setShowNewReminder(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="new-reminder-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Reminder
            </Button>
          </div>
          
          {reminders.length === 0 ? (
            <div className="bg-white p-8 rounded-xl border text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No reminders set</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reminders.map(reminder => (
                <div key={reminder.id} className="bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Bell className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">{reminder.title}</h4>
                      {reminder.description && (
                        <p className="text-sm text-gray-500">{reminder.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {reminder.reminder_date} {reminder.reminder_time && `at ${reminder.reminder_time}`}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => dismissReminder(reminder.id)}>
                    Dismiss
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Task Dialog */}
      <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <Input 
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                placeholder="Task title..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea 
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Task description..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
                <Select value={newTask.assigned_to_id} onValueChange={(v) => setNewTask({...newTask, assigned_to_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.user_id || emp.id}>{emp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <Input 
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Select value={newTask.category} onValueChange={(v) => setNewTask({...newTask, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="follow_up">Follow Up</SelectItem>
                    <SelectItem value="document_review">Document Review</SelectItem>
                    <SelectItem value="application">Application</SelectItem>
                    <SelectItem value="communication">Communication</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowNewTask(false)}>Cancel</Button>
              <Button onClick={createTask} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Template Dialog */}
      <Dialog open={showNewTemplate} onOpenChange={setShowNewTemplate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
              <Input 
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder="e.g., Welcome Email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
              <Input 
                value={newTemplate.subject}
                onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})}
                placeholder="Email subject line..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate({...newTemplate, category: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="application">Application</SelectItem>
                  <SelectItem value="offer">Offer</SelectItem>
                  <SelectItem value="visa">Visa</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Body (HTML) *</label>
              <Textarea 
                value={newTemplate.body}
                onChange={(e) => setNewTemplate({...newTemplate, body: e.target.value})}
                placeholder="Email body... Use {{placeholder}} for dynamic content"
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">
                Available placeholders: student_name, counselor_name, course_name, university_name
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowNewTemplate(false)}>Cancel</Button>
              <Button onClick={createTemplate} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Reminder Dialog */}
      <Dialog open={showNewReminder} onOpenChange={setShowNewReminder}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <Input 
                value={newReminder.title}
                onChange={(e) => setNewReminder({...newReminder, title: e.target.value})}
                placeholder="Reminder title..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea 
                value={newReminder.description}
                onChange={(e) => setNewReminder({...newReminder, description: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <Input 
                  type="date"
                  value={newReminder.reminder_date}
                  onChange={(e) => setNewReminder({...newReminder, reminder_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <Input 
                  type="time"
                  value={newReminder.reminder_time}
                  onChange={(e) => setNewReminder({...newReminder, reminder_time: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowNewReminder(false)}>Cancel</Button>
              <Button onClick={createReminder} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Reminder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskHub;
